/// <reference types="vite/client" />

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './axios';
import { API_ENDPOINTS } from './endpoints';
import { queryKeys } from '../lib/queryClient';
import toast from 'react-hot-toast';

type IdParam = number | string;

interface DriverRow extends Record<string, unknown> {
  Id?: IdParam;
  id?: IdParam;
  DriverId?: IdParam;
  driverId?: IdParam;
  AccountName?: string;
  accountName?: string;
  DriverName?: string;
  driverName?: string;
  MobileNo?: string;
  mobileNo?: string;
  DriverType?: string;
  driverType?: string;
  CompanyId?: IdParam;
  companyId?: IdParam;
  CompanyRefId?: IdParam;
  companyRefId?: IdParam;
  Active?: number | string | boolean;
  active?: number | string | boolean;
  leaves?: any[];
}

export interface NormalizedDriverRow extends DriverRow {
  id: IdParam;
  name: string;
  accountName: string;
  mobileNo: string;
  driverType: string | null;
  companyId: IdParam | null;
  active: boolean;
  Id: IdParam;
  AccountName: string;
  DriverName: string;
  MobileNo: string;
  leaves?: any[];
}

const toPositiveNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const toActiveFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  const text = String(value ?? '').trim().toLowerCase();
  if (text === 'false' || text === '0' || text === 'no') {
    return false;
  }

  return Number(value ?? 1) !== 0;
};

const normalizeDriverComboRow = (
  row: DriverRow = {}
): NormalizedDriverRow | null => {
  const id = row.Id ?? row.id ?? row.DriverId ?? row.driverId;
  if (!id) {
    return null;
  }

  const driverName = String(
    row.DriverName ?? row.driverName ?? row.AccountName ?? row.accountName ?? ''
  ).trim();
  const mobileNo = String(row.MobileNo ?? row.mobileNo ?? '').trim();
  const accountName = String(
    row.AccountName ??
      row.accountName ??
      (driverName && mobileNo ? `${driverName}-${mobileNo}` : driverName)
  ).trim();

  if (!accountName) {
    return null;
  }

  return {
    ...row,
    id,
    name: accountName,
    accountName,
    mobileNo,
    driverType: String(row.DriverType ?? row.driverType ?? '').trim() || null,
    companyId:
    row.CompanyId ?? row.companyId ?? row.CompanyRefId ?? row.companyRefId ?? null,
    active: toActiveFlag(row.Active ?? row.active ?? 1),
    Id: id,
    AccountName: accountName,
    DriverName: driverName || accountName,
    MobileNo: mobileNo,
    leaves: row.leaves ?? [],
  };
};

const normalizeDriverComboResponse = (
  response: unknown
): NormalizedDriverRow[] => {
  const record = response as { Data1?: unknown; data1?: unknown; data?: unknown } | null;
  const rows = Array.isArray(record?.Data1)
    ? record.Data1
    : Array.isArray(record?.data1)
      ? record.data1
      : Array.isArray(record?.data)
        ? record.data
        : Array.isArray(response)
          ? response
          : [];

  return (rows as DriverRow[])
    .map(normalizeDriverComboRow)
    .filter(Boolean) as NormalizedDriverRow[];
};

const filterDrivers = (
  rows: NormalizedDriverRow[],
  companyId: number | string,
  type = ''
): NormalizedDriverRow[] => {
  const normalizedCompanyId = toPositiveNumber(companyId);
  const normalizedType = String(type ?? '').trim().toLowerCase();

  return rows
    .filter((row) => {
      const rowCompanyId = toPositiveNumber(row.companyId);
      const matchesCompany =
        !normalizedCompanyId || !rowCompanyId || rowCompanyId === normalizedCompanyId;
      const matchesType =
        !normalizedType ||
        !row.driverType ||
        row.driverType.toLowerCase() === normalizedType;

      return matchesCompany && row.active && matchesType;
    })
    .sort((left, right) => left.accountName.localeCompare(right.accountName));
};

export const driverApi = {
  getDriverCombo: async (
    companyId: number | string,
    type = ''
  ): Promise<NormalizedDriverRow[]> => {
    if (!companyId) {
      return [];
    }

    try {
      const { data } = await api.get(API_ENDPOINTS.DRIVER.SELECT_ALL_DETAILS, {
        params: { companyId }
      });
      const rows = normalizeDriverComboResponse(data);
      return filterDrivers(rows, companyId, type);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[Driver API] driver master list lookup failed', error);
      }
      return [];
    }
  },

  getDriversPaginated: async (payload: { companyId: number; startIndex: number; pageCount: number; keyword: string; column: string }) => {
    try {
      const response = await api.get(API_ENDPOINTS.DRIVER.SEARCH_PAGINATED, {
        params: {
          companyId: payload.companyId,
          startIndex: payload.startIndex,
          pageCount: payload.pageCount,
          keyword: payload.keyword,
          column: payload.column
        }
      });
      if (response) {
        if (Array.isArray(response.data)) return response.data;
        return response.data?.Data1?.items ?? response.data?.Data1 ?? response.data?.items ?? response.data ?? [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching paginated drivers:', error);
      throw error;
    }
  },

  /**
   * Soft delete. The Spring endpoint marks the driver Active = 2 - the same
   * marker the legacy screen wrote - so RTI, fuel and planning history that
   * points at this driver stays intact. Returns 204 with no body.
   */
  deleteDriver: async (id: number) => {
    try {
      await api.delete(API_ENDPOINTS.DRIVER.MASTER_DELETE(id));
    } catch (error) {
      console.error(`Error deleting driver ${id}:`, error);
      throw error;
    }
  },

  /** Brings a soft-deleted driver back as active. */
  restoreDriver: async (id: number) => {
    const { data } = await api.put(API_ENDPOINTS.DRIVER.MASTER_RESTORE(id));
    return data;
  }
};

export const useDriverCombo = (
  companyId: number | string | null | undefined,
  type = ''
) => {
  const normalizedCompanyId = Number(companyId);
  const hasValidCompanyId =
    Number.isInteger(normalizedCompanyId) && normalizedCompanyId > 0;

  return useQuery({
    queryKey: queryKeys.drivers.list(normalizedCompanyId, type),
    queryFn: () => driverApi.getDriverCombo(normalizedCompanyId, type),
    enabled: hasValidCompanyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useDriversPaginated = (payload: { companyId: number; startIndex: number; pageCount: number; keyword: string; column: string }) => {
  return useQuery({
    queryKey: queryKeys.drivers.paginated(payload),
    queryFn: () => driverApi.getDriversPaginated(payload),
    enabled: !!payload.companyId,
    staleTime: 0,
  });
};

export const useDeleteDriver = () => {
  const queryClient = useQueryClient();

  // The endpoint answers 204 with no body, so a thrown error is the only
  // failure signal - checking the (absent) payload would report a false error.
  return useMutation({
    mutationFn: (data: { id: number; comId?: number }) => driverApi.deleteDriver(data.id),
    onSuccess: () => {
      toast.success("Driver deleted successfully");
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.paginatedAll() });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete driver");
    }
  });
};

/** Brings a soft-deleted driver back as active. */
export const useRestoreDriver = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => driverApi.restoreDriver(id),
    onSuccess: () => {
      toast.success("Driver restored");
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.paginatedAll() });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to restore driver");
    }
  });
};

export default driverApi;
